import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import Decimal from "decimal.js";
import { Suspense, useEffect, useMemo } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  bypassApprovalRequestReasonValues,
  fXTradeTransferGroupKindValues,
} from "~/gql/client/enumValues";
import type {
  AccountInput,
  BypassApprovalRequestReason,
  FxTradeTransferGroupKind,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/transfers_/new")({
  head: () => opsHead("New Transfer"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const bypassApprovalsOptionLabels: Record<BypassApprovalRequestReason, string> =
  {
    APPROVAL_NOT_APPLICABLE: "Approval not applicable",
    DO_NOT_BYPASS_APPROVAL_POLICY: "Do not bypass approval policy",
    OPERATOR_BYPASSED_POLICY: "Bypass approval policy",
    TRANSFER_CREATED_RETROACTIVELY: "Transfer created retroactively",
  };

const bypassApprovalsOptions = bypassApprovalRequestReasonValues.map((v) => ({
  value: v,
  label: bypassApprovalsOptionLabels[v],
}));

const formSchema = z.object({
  amount: z
    .custom<Decimal>(Decimal.isDecimal, {
      message: "Please enter an amount",
    })
    .refine((val) => val?.gt(0), "Amount must be positive")
    .describe("Amount"),
  amountSpecifiedIn: z
    .string()
    .nonempty("Required")
    .refine(
      (val) => {
        const parts = val.split(":");
        if (parts.length !== 3) {
          return false;
        }

        const [side, kind] = parts;
        return (
          (side === "SOURCE" || side === "DESTINATION") &&
          (kind === "CURRENCY" || kind === "UNIT")
        );
      },
      {
        message: "Invalid",
      },
    ),
  bypassApprovalsOption: z.enum(bypassApprovalRequestReasonValues, {
    error: "Required",
  }),
  date: z.date({ error: "Required" }),
  description: z.string().optional(),
  destinationAccountId: z.string().nonempty({ message: "Required" }),
  fxTradeId: z.string().optional(),
  fxTradeTransferPurpose: z.enum(fXTradeTransferGroupKindValues).optional(),
  organisationId: z.string().nonempty({ message: "Required" }),
  skipRouting: z.boolean(),
  sourceAssetId: z.string().nonempty({ message: "Required" }),
  suppressNotifications: z.boolean(),
});

type FormData = z.input<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewTransferForm {
        organisations {
          ...AdminNewTransferFormOrg
        }
      }

      fragment AdminNewTransferFormOrg on Organisation {
        id
        name
        beneficiaryAccounts {
          id
          ...BeneficiaryAssetCardFields
        }
        businessEntities {
          id
          name
          assets(includeInactive: true) {
            id
            active
            currency
            ...AssetCardFields
            ... on FixedIncomeFundAsset {
              product {
                id
                name
                ... on FundProduct {
                  unit {
                    id
                  }
                }
              }
            }
          }
          fxTrades {
            id
            status
            valueDate
            sellAmount {
              amount
              currency
            }
            buyAmount {
              amount
              currency
            }
            tradeType
          }
        }
        transferApprovalPolicy {
          id
          userReviewRequired
        }
      }
    `),
  );

  const [createTransferMutation, { loading }] = useMutation(
    graphql(`
      mutation AdminNewTransferForm_Create($input: CreateTransferAdminInput!) {
        createTransferAdmin(input: $input) {
          transfer {
            id
            paymentReference
          }
        }
      }
    `),
  );

  if (!data.organisations) {
    throw new Error("expected organisations");
  }

  const form = useAppForm({
    defaultValues: {
      bypassApprovalsOption: "DO_NOT_BYPASS_APPROVAL_POLICY",
      skipRouting: false,
      suppressNotifications: false,
    } as FormData,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      // note: format of this string is validated in the form schema
      const [unvalidatedSide, _kind, unit] = value.amountSpecifiedIn.split(":");
      const side = z.enum(["SOURCE", "DESTINATION"]).parse(unvalidatedSide);

      let destinationAccount: AccountInput;

      if (selectedDestinationAsset) {
        destinationAccount = {
          assetId: value.destinationAccountId,
        };
      } else if (selectedDestinationBeneficiaryAccount) {
        destinationAccount = {
          beneficiaryAccountId: value.destinationAccountId,
        };
      } else {
        throw new Error("expected either beneficiary account or asset");
      }

      const defaultErrorMessage =
        "There was an error submitting your form. Please try again later.";

      await createTransferMutation({
        variables: {
          input: {
            amount: value.amount,
            bypassApprovalPolicyOption: value.bypassApprovalsOption,
            date: value.date,
            description: value.description ?? "",
            destinationAccount,
            organisationId: value.organisationId,
            side,
            skipRouting: value.skipRouting,
            sourceAssetId: value.sourceAssetId,
            unit,
            suppressNotifications: value.suppressNotifications,
            fxTradeId:
              value.fxTradeId && value.fxTradeId !== "NONE"
                ? value.fxTradeId
                : undefined,
            fxTradeTransferPurpose: value.fxTradeTransferPurpose,
          },
        },
        onCompleted: (data) => {
          if (!data.createTransferAdmin?.transfer.id) {
            toast({
              kind: "error",
              autoHide: false,
              title: "Failed to create transfer",
              description: defaultErrorMessage,
            });
            throw new Error("failed to create transfer");
          } else {
            toast({
              kind: "success",
              autoHide: true,
              title: "Created transfer",
              description: `Created new transfer ${data.createTransferAdmin.transfer.paymentReference}`,
            });
            void navigate({
              to: "/transfers/$transferId",
              params: { transferId: data.createTransferAdmin.transfer.id },
            });
          }
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || defaultErrorMessage;
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to create transfer",
            description: errorMessage,
          });
          throw error;
        },
      });
    },
  });

  const organisationOptions = data.organisations
    .map((org) => ({
      value: org.id,
      label: org.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = data.organisations.find(
    (o) => o.id === selectedOrganisationId,
  );

  const selectedSourceAssetId = useStore(
    form.store,
    (state) => state.values.sourceAssetId,
  );

  const destinationAccountId = useStore(
    form.store,
    (state) => state.values.destinationAccountId,
  );

  const allOrgAssets = useMemo(
    () =>
      selectedOrganisation?.businessEntities?.flatMap(
        (be) =>
          be.assets?.filter(
            (asset) =>
              asset.active ||
              asset.assetStatus === "PENDING" ||
              asset.assetStatus === "SCHEDULED",
          ) ?? [],
      ) ?? [],
    [selectedOrganisation],
  );

  const selectedSourceAsset = allOrgAssets.find(
    (a) => a.id === selectedSourceAssetId,
  );

  const selectedDestinationAsset = allOrgAssets.find(
    (a) => a.id === destinationAccountId,
  );

  const selectedDestinationBeneficiaryAccount =
    selectedOrganisation?.beneficiaryAccounts?.find(
      (b) => b.id === destinationAccountId,
    );

  // Used only to find FX trades associated with the source asset's business entity.
  const sourceBusinessEntity = selectedOrganisation?.businessEntities?.find(
    (be) => be.assets?.some((a) => a.id === selectedSourceAssetId),
  );

  const sourceAssets = useMemo(
    () =>
      allOrgAssets
        .filter(
          (a) =>
            a.id !== selectedDestinationAsset?.id &&
            a.assetKind === "PRIMARY_MANAGED",
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allOrgAssets, selectedDestinationAsset?.id],
  );

  const destinationAssets = useMemo(
    () =>
      allOrgAssets
        .filter(
          (a) =>
            a.id !== selectedSourceAssetId && a.assetKind === "PRIMARY_MANAGED",
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allOrgAssets, selectedSourceAssetId],
  );

  const orgBeneficiaryAccounts = selectedOrganisation?.beneficiaryAccounts;

  const destinationCurrency =
    selectedDestinationAsset?.currency ??
    selectedDestinationBeneficiaryAccount?.currency;

  const amountSpecifiedInOptions = useMemo(() => {
    const opts: {
      label: string;
      value: string;
    }[] = [];
    if (selectedSourceAsset?.currency) {
      opts.push({
        label: `${selectedSourceAsset.currency}`,
        value: `SOURCE:CURRENCY:${selectedSourceAsset.currency}`,
      });
    }

    if (
      destinationCurrency &&
      destinationCurrency !== selectedSourceAsset?.currency
    ) {
      opts.push({
        label: `${destinationCurrency}`,
        value: `DESTINATION:CURRENCY:${destinationCurrency}`,
      });
    }

    const sourceFundProduct =
      selectedSourceAsset?.__typename === "FixedIncomeFundAsset" &&
      selectedSourceAsset.product?.__typename === "FundProduct"
        ? selectedSourceAsset.product
        : undefined;
    if (sourceFundProduct?.unit?.id) {
      opts.push({
        label: `Units of ${sourceFundProduct.name}`,
        value: `SOURCE:UNIT:${sourceFundProduct.unit.id}`,
      });
    }

    const destinationFundProduct =
      selectedDestinationAsset?.__typename === "FixedIncomeFundAsset" &&
      selectedDestinationAsset.product?.__typename === "FundProduct"
        ? selectedDestinationAsset.product
        : undefined;
    if (
      destinationFundProduct?.unit?.id &&
      destinationFundProduct?.unit?.id !== sourceFundProduct?.unit?.id
    ) {
      opts.push({
        label: `Units of ${destinationFundProduct.name}`,
        value: `DESTINATION:UNIT:${destinationFundProduct.unit.id}`,
      });
    }

    return opts;
  }, [selectedSourceAsset, destinationCurrency, selectedDestinationAsset]);

  const sourceIsFxAccount = selectedSourceAsset?.class === "FX";
  const destinationIsFxAccount = selectedDestinationAsset?.class === "FX";

  const relevantFxTrades = useMemo(() => {
    return (
      sourceBusinessEntity?.fxTrades?.filter((trade) => {
        if (trade.status !== "PLACED" && trade.status !== "OPEN") {
          return false;
        }

        // If destination is FX, only show trades where sell currency matches
        if (destinationIsFxAccount && selectedDestinationAsset?.currency) {
          return (
            trade.sellAmount.currency === selectedDestinationAsset.currency
          );
        }
        // If source is FX, only show trades where buy currency matches
        if (sourceIsFxAccount && selectedSourceAsset?.currency) {
          return trade.buyAmount.currency === selectedSourceAsset.currency;
        }

        return false;
      }) ?? []
    );
  }, [
    destinationIsFxAccount,
    sourceIsFxAccount,
    selectedDestinationAsset?.currency,
    selectedSourceAsset?.currency,
    sourceBusinessEntity?.fxTrades,
  ]);

  const fxTradeOptions = useMemo(() => {
    return [
      {
        label: "Do not link to FX trade",
        value: "NONE",
      },
      ...relevantFxTrades.map((trade) => {
        const tradeDate = format(new Date(trade.valueDate), "d MMM yyyy");
        return {
          label: `${tradeDate} ${prettyPrintEnum(trade.tradeType)}: $${trade.sellAmount.amount} ${trade.sellAmount.currency} → $${trade.buyAmount.amount} ${trade.buyAmount.currency}`,
          value: trade.id,
        };
      }),
    ];
  }, [relevantFxTrades]);

  const shouldShowFxTradeFields =
    relevantFxTrades.length > 0 &&
    !(sourceIsFxAccount && destinationIsFxAccount);

  const selectedFxTradeId = useStore(
    form.store,
    (state) => state.values.fxTradeId,
  );
  const selectedFxTrade = relevantFxTrades.find(
    (t) => t.id === selectedFxTradeId && selectedFxTradeId !== "NONE",
  );

  const fxTradeTransferPurposeOptions = useMemo(() => {
    const opts: {
      label: string;
      value: FxTradeTransferGroupKind;
    }[] = [];

    // If source is FX, only show withdrawal
    if (sourceIsFxAccount) {
      opts.push({
        label: "FX trade withdrawal",
        value: "WITHDRAWAL",
      });
    }

    // If destination is FX, show funding and margin call options
    if (destinationIsFxAccount) {
      opts.push({
        label: "Funding FX Trade",
        value: "FUNDING",
      });

      if (selectedFxTrade?.tradeType === "FIXED_FORWARD") {
        opts.push({
          label: "Margin call",
          value: "MARGIN",
        });
      }
    }

    return opts;
  }, [sourceIsFxAccount, destinationIsFxAccount, selectedFxTrade]);

  useEffect(() => {
    if (amountSpecifiedInOptions?.length === 1) {
      form.setFieldValue(
        "amountSpecifiedIn",
        amountSpecifiedInOptions[0].value,
      );
    }
  }, [
    amountSpecifiedInOptions?.length,
    amountSpecifiedInOptions?.[0]?.value,
    form,
  ]);

  const selectedFxTradeTransferPurpose = useStore(
    form.store,
    (state) => state.values.fxTradeTransferPurpose,
  );

  useEffect(() => {
    if (
      selectedFxTradeId &&
      selectedFxTradeId !== "NONE" &&
      fxTradeTransferPurposeOptions?.length === 1 &&
      !selectedFxTradeTransferPurpose
    ) {
      form.setFieldValue(
        "fxTradeTransferPurpose",
        fxTradeTransferPurposeOptions[0].value,
      );
    }
  }, [
    selectedFxTradeId,
    selectedFxTradeTransferPurpose,
    fxTradeTransferPurposeOptions?.length,
    fxTradeTransferPurposeOptions?.[0]?.value,
    form,
  ]);

  return (
    <AdminPageContainer testId="admin-transfers-new2">
      <AdminPageHeader title="New Transfer" subtitle="Create a new transfer" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex max-w-lg flex-col gap-8">
          <form.AppField
            name="organisationId"
            listeners={{
              onChange: () => {
                form.resetField("sourceAssetId");
                form.resetField("destinationAccountId");
                form.resetField("fxTradeId");
                form.resetField("fxTradeTransferPurpose");
              },
            }}
          >
            {(field) => (
              <field.SelectField label="Organisation" placeholder="Select...">
                {organisationOptions.map((o) => (
                  <field.SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField
            name="sourceAssetId"
            listeners={{
              onChange: () => {
                form.resetField("fxTradeId");
                form.resetField("fxTradeTransferPurpose");
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Source asset"
                disabled={!selectedOrganisationId}
                placeholder={
                  !selectedOrganisationId
                    ? "Select organisation first"
                    : "Select..."
                }
              >
                {sourceAssets.map((a) => (
                  <field.SelectItem key={a.id} value={a.id}>
                    <AccountCard
                      account={a}
                      subText={[
                        "BUSINESS_ENTITY",
                        "BALANCE_FULL",
                        "IDENTIFIER",
                      ]}
                    />
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="destinationAccountId">
            {(field) => (
              <field.SelectField
                label="Destination account"
                disabled={!selectedOrganisationId}
                placeholder={
                  !selectedOrganisationId
                    ? "Select organisation first"
                    : "Select..."
                }
              >
                {destinationAssets.map((a) => (
                  <field.SelectItem key={a.id} value={a.id}>
                    <AccountCard
                      account={a}
                      subText={[
                        "BUSINESS_ENTITY",
                        "BALANCE_FULL",
                        "IDENTIFIER",
                      ]}
                    />
                  </field.SelectItem>
                ))}
                {orgBeneficiaryAccounts?.map((b) => (
                  <field.SelectItem key={b.id} value={b.id}>
                    <AccountCard
                      account={b}
                      subText={["BUSINESS_ENTITY", "IDENTIFIER"]}
                    />
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField
            name="skipRouting"
            listeners={{
              onChange: ({ value }) => {
                if (value) {
                  alert(
                    "Warning - this will create a single transfer between the selected accounts, bypassing transfer routing via the hub account. Should be used only in exceptional circumstances. Contact engineering if unsure.",
                  );
                }
              },
            }}
          >
            {(field) => <field.SwitchField label="Skip transfer routing" />}
          </form.AppField>

          <form.AppField name="amountSpecifiedIn">
            {(field) => (
              <field.SelectField
                label="Amount specified in"
                disabled={!selectedSourceAssetId || !destinationAccountId}
                placeholder={
                  !selectedSourceAssetId || !destinationAccountId
                    ? "Select source and destination accounts first"
                    : "Select..."
                }
              >
                {amountSpecifiedInOptions.map((o) => (
                  <field.SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          {shouldShowFxTradeFields && (
            <>
              <form.AppField
                name="fxTradeId"
                listeners={{
                  onChange: () => {
                    form.resetField("fxTradeTransferPurpose");
                  },
                }}
              >
                {(field) => (
                  <field.SelectField
                    label="FX Trade (optional)"
                    placeholder="Select..."
                  >
                    {fxTradeOptions.map((opt) => (
                      <field.SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </field.SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>

              {selectedFxTradeId && selectedFxTradeId !== "NONE" && (
                <form.AppField name="fxTradeTransferPurpose">
                  {(field) => (
                    <field.SelectField
                      label="Transfer purpose"
                      placeholder="Select..."
                    >
                      {fxTradeTransferPurposeOptions.map((opt) => (
                        <field.SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </field.SelectItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>
              )}
            </>
          )}

          <form.AppField name="amount">
            {(field) => (
              <form.Subscribe
                selector={(state) => state.values.amountSpecifiedIn}
              >
                {(amountSpecifiedIn) => (
                  <field.UnitAmountField
                    align="start"
                    label="Amount"
                    disabled={!amountSpecifiedIn}
                    placeholder={
                      !amountSpecifiedIn
                        ? "Select how amount will be specified first"
                        : undefined
                    }
                  />
                )}
              </form.Subscribe>
            )}
          </form.AppField>
          <form.AppField name="date">
            {(field) => (
              <div className="flex justify-start">
                <field.DatePickerField
                  label="Date"
                  placeholder={
                    !selectedSourceAssetId || !destinationAccountId
                      ? "Select source and destination accounts first"
                      : "Pick a date"
                  }
                />
              </div>
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => <field.TextareaField label="Description" />}
          </form.AppField>
          <div className="flex flex-col gap-2">
            <form.AppField name="bypassApprovalsOption">
              {(field) => (
                <field.SelectField label="Bypass approvals?">
                  {bypassApprovalsOptions.map((v) => (
                    <field.SelectItem key={v.value} value={v.value}>
                      {v.label}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            {selectedOrganisation && (
              <div className="">
                {selectedOrganisation.transferApprovalPolicy
                  ?.userReviewRequired ? (
                  <Txt variant="body" tone="danger">
                    This organisation currently requires approvals.
                  </Txt>
                ) : (
                  <Txt variant="description">
                    This organisation does not currently require approvals.
                  </Txt>
                )}
              </div>
            )}
          </div>

          <form.AppField name="suppressNotifications">
            {(field) => <field.SwitchField label="Do not send notifications" />}
          </form.AppField>

          <div className="flex justify-end">
            <Button variant="primary" type="submit" disabled={loading}>
              Create transfer
            </Button>
          </div>
        </div>
      </form>
    </AdminPageContainer>
  );
}
